import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startOfWeek, endOfWeek } from 'date-fns';
import { scheduleKeys } from './queryKeys';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { mapShiftRpcError } from '@/hooks/useSchedulingMutations';
import { Shift } from '@/services/schedule/types';

export const useClaimShift = (
  profileId: string | undefined,
  profile: any,
  currentWeek: Date,
  companyId: string | undefined
) => {
  const queryClient = useQueryClient();
  const weekStartISO = startOfWeek(currentWeek).toISOString();
  const weekEndISO = endOfWeek(currentWeek).toISOString();
  
  return useMutation({
    mutationFn: async (shiftId: string) => {
      const { data, error } = await supabase.rpc('claim_shift', {
        p_shift_id: shiftId
      });
      
      if (error) {
        if (error.message?.includes('already_claimed')) {
          // CRITICAL: Must refresh open shifts on already_claimed error
          await queryClient.invalidateQueries({
            queryKey: scheduleKeys.openShifts(companyId || '', weekStartISO, weekEndISO)
          });
          throw new Error('already_claimed');
        }
        throw error;
      }
      
      return { data, shiftId };
    },
    
    onMutate: async (shiftId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: scheduleKeys.myShifts(profileId || '', weekStartISO, weekEndISO)
      });
      await queryClient.cancelQueries({
        queryKey: scheduleKeys.openShifts(companyId || '', weekStartISO, weekEndISO)
      });
      
      // Snapshot previous values
      const previousMyShifts = queryClient.getQueryData(
        scheduleKeys.myShifts(profileId || '', weekStartISO, weekEndISO)
      );
      const previousOpenShifts = queryClient.getQueryData(
        scheduleKeys.openShifts(companyId || '', weekStartISO, weekEndISO)
      );
      
      return { previousMyShifts, previousOpenShifts, shiftId };
    },
    
    onSuccess: ({ data, shiftId }, _, context) => {
      const shiftData = data[0];
      const claimedShift: Shift = {
        id: shiftData.id,
        profile_id: profileId || null,
        start_time: shiftData.start_time,
        end_time: shiftData.end_time,
        note: shiftData.note,
        employee_name: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'You',
        status: 'active' as const
      };
      
      // Update my shifts
      queryClient.setQueryData(
        scheduleKeys.myShifts(profileId || '', weekStartISO, weekEndISO),
        (old: any[]) => [...(old || []), claimedShift].sort((a, b) => 
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
        )
      );
      
      // Remove from open shifts
      queryClient.setQueryData(
        scheduleKeys.openShifts(companyId || '', weekStartISO, weekEndISO),
        (old: any[]) => (old || []).filter(shift => shift.id !== shiftId)
      );
      
      toast.success('Shift claimed successfully!');
    },
    
    onError: (error, shiftId, context) => {
      // Rollback optimistic updates
      if (context?.previousMyShifts) {
        queryClient.setQueryData(
          scheduleKeys.myShifts(profileId || '', weekStartISO, weekEndISO),
          context.previousMyShifts
        );
      }
      if (context?.previousOpenShifts) {
        queryClient.setQueryData(
          scheduleKeys.openShifts(companyId || '', weekStartISO, weekEndISO),
          context.previousOpenShifts
        );
      }
      
      const errorMessage = error.message === 'already_claimed' 
        ? 'This shift was just claimed by someone else.'
        : mapShiftRpcError(error.message);
      
      toast.error(errorMessage);
    },
    
    onSettled: () => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.myShifts(profileId || '', weekStartISO, weekEndISO)
      });
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.openShifts(companyId || '', weekStartISO, weekEndISO)
      });
    }
  });
};
